// Invoice Comparison page — Wave D follow-up.
//
// Mirrors the legacy `tricivo_system/operations/invoice_management.php`
// "Invoice Comparison" tab. The legacy parses the supplier PDF in the
// browser via PDF.js; this React page POSTs the file to
// `POST /api/v1/invoices/compare` so all parsing is server-side. The
// response shape (`summary` + `lines` + `unmatched_internal`) is the same
// shape rendered by the legacy table — match badges, side-by-side amounts,
// and a separate "in our records but not on invoice" list at the bottom.
//
// Filters at the top mirror the legacy header: Work Source dropdown +
// month/date range. Submit triggers the upload and renders the result
// inline below.

import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileSearch, Upload } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Select } from '../../components/form/Select';
import { DatePicker } from '../../components/form/DatePicker';
import { Table, type Column } from '../../components/ui/Table/Table';
import {
  useInvoiceCompare,
  useInvoiceWorkSources,
} from './hooks/useInvoices';
import type {
  InvoiceCompareLineDto,
  InvoiceCompareMatchStatus,
  InvoiceCompareResponse,
  InvoiceCompareUnmatchedInternalDto,
} from '../../types/api';
import styles from './InvoiceComparePage.module.css';

const MATCH_LABEL: Record<InvoiceCompareMatchStatus, string> = {
  collected: 'Collected',
  booked: 'Booked',
  pending: 'Pending',
  cancelled: 'Cancelled',
  not_found: 'Not in records',
};

const MATCH_CLASS: Record<InvoiceCompareMatchStatus, string | undefined> = {
  collected: styles.matchCollected,
  booked: styles.matchBooked,
  pending: styles.matchPending,
  cancelled: styles.matchCancelled,
  not_found: styles.matchNotFound,
};

function formatMoney(n: number | null): string {
  if (n === null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function InvoiceComparePage(): JSX.Element {
  const navigate = useNavigate();

  const workSources = useInvoiceWorkSources();
  const compare = useInvoiceCompare();

  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [sourceId, setSourceId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const sourceOptions = useMemo(() => {
    const rows = workSources.data?.work_sources ?? [];
    return [
      { value: '', label: 'Select work source…', disabled: true },
      ...rows.map((r) => ({ value: String(r.id), label: r.name })),
    ];
  }, [workSources.data]);

  function handleFiles(files: FileList | null) {
    setSubmitError(null);
    if (!files || files.length === 0) return;
    const f = files[0];
    if (!f) return;
    if (f.type !== 'application/pdf') {
      setSubmitError('PDF files only.');
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      setSubmitError('File exceeds the 2 MiB upload cap.');
      return;
    }
    setFile(f);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (file === null) {
      setSubmitError('Choose a PDF first.');
      return;
    }
    const sourceNum = Number.parseInt(sourceId, 10);
    if (!Number.isFinite(sourceNum) || sourceNum <= 0) {
      setSubmitError('Pick a work source.');
      return;
    }
    if (!dateFrom || !dateTo) {
      setSubmitError('Pick a date range.');
      return;
    }
    compare.mutate({ file, source_id: sourceNum, date_from: dateFrom, date_to: dateTo });
  }

  const result: InvoiceCompareResponse | undefined = compare.data;

  return (
    <form className={styles.page} onSubmit={handleSubmit}>
      <PageHeader
        title="Invoice Comparison"
        subtitle="Upload a supplier invoice PDF, pick the work source and month, then match line-by-line against your records."
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/collections/invoices')}
            type="button"
          >
            Back to invoices
          </Button>
        }
      />

      <Card title="Supplier invoice">
        <div className={styles.uploadRow}>
          <label className={styles.fileLabel}>
            <span>Invoice PDF</span>
            <div
              className={dragOver ? styles.dropZoneActive : styles.dropZone}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFiles(e.dataTransfer.files);
              }}
            >
              <Upload size={20} aria-hidden />
              <p>{file ? file.name : 'Drop a PDF here or use the field below.'}</p>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => handleFiles(e.target.files)}
                aria-label="Invoice PDF"
              />
            </div>
          </label>
          <label className={styles.fieldLabel}>
            <span>Work source</span>
            <Select
              aria-label="Work source"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              options={sourceOptions}
            />
          </label>
          <label className={styles.fieldLabel}>
            <span>Date from</span>
            <DatePicker
              aria-label="Date from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label className={styles.fieldLabel}>
            <span>Date to</span>
            <DatePicker
              aria-label="Date to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
          <Button
            variant="primary"
            type="submit"
            icon={FileSearch}
            loading={compare.isPending}
          >
            Compare
          </Button>
        </div>
        {submitError ? (
          <p className={styles.errorBanner} role="alert">
            {submitError}
          </p>
        ) : null}
        {compare.isError ? (
          <p className={styles.errorBanner} role="alert">
            The server could not parse the PDF. Check the file and try again.
          </p>
        ) : null}
      </Card>

      {result ? (
        <CompareResult result={result} />
      ) : null}
    </form>
  );
}

function CompareResult({ result }: { result: InvoiceCompareResponse }): JSX.Element {
  const { summary, lines, unmatched_internal } = result;

  const lineColumns: Column<InvoiceCompareLineDto>[] = [
    {
      key: 'invoice_reg',
      header: 'Invoice Reg',
      render: (_v, row) => row.invoice_reg,
    },
    {
      key: 'invoice_vehicle',
      header: 'Invoice Vehicle',
      render: (_v, row) => row.invoice_vehicle,
    },
    {
      key: 'invoice_amount',
      header: 'Invoice Amount',
      align: 'right',
      render: (_v, row) => formatMoney(row.invoice_amount),
    },
    {
      key: 'our_status',
      header: 'Our Status',
      render: (_v, row) =>
        row.our_status === null ? <span className={styles.cellMuted}>—</span> : row.our_status,
    },
    {
      key: 'our_customer',
      header: 'Our Customer',
      render: (_v, row) =>
        row.our_customer ?? <span className={styles.cellMuted}>—</span>,
    },
    {
      key: 'collection_date',
      header: 'Collection Date',
      render: (_v, row) => formatDate(row.collection_date),
    },
    {
      key: 'driver',
      header: 'Driver',
      render: (_v, row) =>
        row.driver ?? <span className={styles.cellMuted}>—</span>,
    },
    {
      key: 'our_amount',
      header: 'Our Amount',
      align: 'right',
      render: (_v, row) => formatMoney(row.our_amount),
    },
    {
      key: 'has_receipt',
      header: 'Receipt',
      render: (_v, row) => {
        if (row.has_receipt === null) return <span className={styles.cellMuted}>—</span>;
        return row.has_receipt ? 'Yes' : 'No';
      },
    },
    {
      key: 'match_status',
      header: 'Match',
      render: (_v, row) => (
        <span className={`${styles.matchBadge} ${MATCH_CLASS[row.match_status]}`}>
          {MATCH_LABEL[row.match_status]}
        </span>
      ),
    },
  ];

  const unmatchedColumns: Column<InvoiceCompareUnmatchedInternalDto>[] = [
    { key: 'registration', header: 'Registration', render: (_v, row) => row.registration },
    {
      key: 'make_model',
      header: 'Vehicle',
      render: (_v, row) => row.make_model ?? <span className={styles.cellMuted}>—</span>,
    },
    { key: 'status', header: 'Status', render: (_v, row) => row.status },
    {
      key: 'collected',
      header: 'Collected',
      render: (_v, row) => (row.collected ? 'Yes' : 'No'),
    },
    { key: 'source', header: 'Source', render: (_v, row) => row.source },
  ];

  return (
    <>
      <div className={styles.summaryRow} role="status" aria-label="Comparison summary">
        <Card>
          <div className={styles.summaryTile}>
            <span className={styles.summaryLabel}>Lines on invoice</span>
            <span className={styles.summaryValue}>{summary.total_invoice_lines}</span>
          </div>
        </Card>
        <Card>
          <div className={styles.summaryTile}>
            <span className={styles.summaryLabel}>Matched</span>
            <span className={styles.summaryValue}>{summary.matched}</span>
          </div>
        </Card>
        <Card>
          <div className={styles.summaryTile}>
            <span className={styles.summaryLabel}>Collected</span>
            <span className={styles.summaryValue}>{summary.collected}</span>
          </div>
        </Card>
        <Card>
          <div className={styles.summaryTile}>
            <span className={styles.summaryLabel}>Cancelled</span>
            <span className={styles.summaryValue}>{summary.cancelled}</span>
          </div>
        </Card>
        <Card>
          <div className={styles.summaryTile}>
            <span className={styles.summaryLabel}>Not in records</span>
            <span className={styles.summaryValue}>{summary.not_found}</span>
          </div>
        </Card>
      </div>

      <Card flush>
        <Table
          columns={lineColumns}
          data={lines}
          keyField={(row) => row.invoice_reg}
          emptyState={
            <div className={styles.cellMuted} style={{ padding: '1.5rem', textAlign: 'center' }}>
              No vehicle lines were found in the PDF.
            </div>
          }
          ariaLabel="Invoice comparison lines"
        />
      </Card>

      {unmatched_internal.length > 0 ? (
        <Card title="Records in our system NOT on the invoice">
          <Table
            columns={unmatchedColumns}
            data={unmatched_internal}
            keyField={(row) => row.registration}
            ariaLabel="Internal records not on invoice"
          />
        </Card>
      ) : null}
    </>
  );
}
